//  ----------------------------------------------------------------------------
//  Dependencies
//  ----------------------------------------------------------------------------
//  -- Components ---------------------
import { Check, Close, Flag, MoreVert } from '@mui/icons-material';
import { Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle } from '@mui/material';
import { Dropdown } from 'antd';
import React, { startTransition, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
//  -- Thunks -------------------------
import { handleUpdateCanvasGradeInBatch, handleUpdateCanvasStatus } from 'store/canvas/thunks';
import { handleBatchGradeSubmissions, handleListClassSubmissions, handleReturnSubmission, handleUpdateGrade, handleUpdateSubmission } from 'store/submissions/thunks';
import styled from 'styled-components';
//  -- Constants ----------------------
import * as Sentry from '@sentry/react';
import { useAlert } from 'components/UI/alertSakeBar/alertProvider';
import { debounce, find, get, has, map } from 'lodash';
import moment from "moment";
import { useNavigate } from "react-router-dom";
import { handleUpdateGoolgeGradeInBatch } from "store/googleClassRoom/thunks";
import { handleGradeToSchoology } from 'store/schoology/thunks';
import { AbsentSvg, LateSvg } from "utils/assets/images";
import COLOR from 'utils/constants/color';
import { getConfigForDiff } from 'utils/lib/ebookHelper';
import { useCollectionNotification } from 'utils/methods/firebaseNotification';
import { checkIsManualGradable, isBeforTime } from 'utils/methods/math';
import { v4 } from 'uuid';
import { useUserProfileConfig } from 'store/users/selectors';
import FullScreenLoader from 'components/UI/fullScreenLoader';
import { useGradeBook } from 'components/hooks/gradeBook/useGradeBook';

const COLORSTATUS = ["", COLOR.greentrue, COLOR.orange100, COLOR.red ,"","",COLOR.yellow]
const Poligon = styled.div`
    display:flex;
    flex-direction:row;
    clip-path: polygon(50% 0%, 90% 20%, 100% 60%, 75% 100%, 25% 100%, 0% 60%, 10% 20%);
    height: 18px;
    width: 18px;
    margin-right: 5px;
    background-color:${p => p.bgColor ? p.bgColor : COLOR.green};
`;

const ALLUSER_FLAG = [
  { title: "Excused", value: 1, action: true },
  { title: "Incomplete", value: 2, action: true },
  { title: "Missing", value:3, action: true },
];
const SCHOOLOGY_ALLUSER_FLAG = [
  { title: "Exempt", value: 1, action: true },
  { title: "Incomplete", value: 2, action: true },
  { title: "Missing", value:3, action: true },
  { title: "Absent", value:5, action: true },
  { title: "Late", value:6, action: true },
];
const CANVAS_FLAG = [
  { title: "Excused", value: 1, action: true },
  { title: "Missing", value:3, action: true }
];
const CANVAS_FLAG_WITH_COMPLETE = [
  { title: "Excused", value: 1, action: true },
  { title: "Missing", value:3, action: true },
  { title: "Complete", value:"complete", action: true },
  { title: "Incomplete", value:"incomplete", action: true }
];


//  ----------------------------------------------------------------------------
//  Styles
//  ----------------------------------------------------------------------------
const Button = styled.button`
    align-items: center;
    background-color: ${COLOR.green100};
    border: none;
    border-radius: 2px;
    color: white;
    cursor: pointer;
    display: flex;
    height: 24px;
    margin-left: 4px;
    justify-content: center;
    opacity: 0;
    outline: none;
    padding: 2px;
    transition: all 300ms ease;
    width: 24px;
    cursor: ${p => !p.userActive ? 'default' : 'pointer'};

    &:hover {
        box-shadow: 0 14px 28px rgba(0,0,0,0.25), 0 10px 10px rgba(0,0,0,0.22);
    }
`;

const Input = styled.input`
    border: 1px solid;
    border-radius: 3px;
    box-shadow: none;
    font-size: inherit;
    outline: none;
    padding: 0px 4px;
    transition: all 300ms ease;
    text-align:center;
    width: 40px;
`;

const Form = styled.form`
    algin-items: center;
    color: ${p => p.graded ? 'inherit' : 'red'};
    display: flex;
    flex-direction: row;
    justify-content: center;
    position: relative;
    width: 100%;

    &:hover ${Input} {
        border: 1px solid ${p => p.disabled ? 'white' : 'white'};
    }

    &:hover ${Button} {
        opacity: ${p => p.disabled ? 0 : 1};
        color: white;
    }
`;
const CheckBox = styled.div`
    align-items: center;
    height: 20px;
    display: flex;
    justify-content: center;
    width: 20px;
    margin-left: 4px;
    margin-right: 4px;
    cursor: pointer;
    transition: all 300ms ease;
    border: 2px solid #333;     /* border so it looks like a box */
    border-radius: 4px;         /* slight rounding (optional) */
    background-color: ${props => props.checked ? "#111" : "#fff"}; /* toggle bg */
    color: #fff;                /* checkmark color */
`;
const MenuItem = styled.div`
    display: flex;
    flex-direction: row;
    `;

const StatusText = styled.div`
    position: absolute;
    bottom: -16px;
    font-size: 12px;
    color: ${p => p.color ? p.color : '#1b1e23'};
    font-weight: normal;
`;

const ResubmittedStatusText = styled.div`
    position: absolute;
    top: -16px;
    font-size: 12px;
    color: ${p => p.color ? p.color : '#1b1e23'};
    font-weight: normal;
`;

const MaxPointText = styled.span`
color:${COLOR.gray100}
`;
//  ----------------------------------------------------------------------------
//  Component
//  ----------------------------------------------------------------------------
//
//  Grade editing form
//
//  -- Description
//  Allows editing of grades/submissions
//
//  -- Props
//  ...
//
//  -- State
//  ...
//

const hasGradeInA_F = (val) => {
    if (typeof val === "string") {
        const hasInGrade = [{ key: 'A', value: 90 }, { key: 'B', value: 70 }, { key: 'C', value: 50 }, { key: 'D', value: 30 }, { key: 'F', value: 10 }].find(k => k.key.toLowerCase() === val.toLowerCase())
        if (hasInGrade) {
            return ({
                value: hasInGrade.value,
                hasValue: true
            })
        }
        return ({
            value: val,
            hasValue: false
        })
    } else {
        return ({
            value: val,
            hasValue: false
        })
    }

}


const GradeEditor = React.forwardRef(({
    assignmentId,
    data,
    reviewAssignment,
    submission,
    userId,
    value,
    isReviewAccount,
    disabled,
    scale_1_5,
    gradebook_a_f,
    tempData,
    assignment,
    user,
    currentClass,
    assignments,
    onFlagMenuViewChange,
    onFocus,
    onBlur,
    isTbg,
    isLate,
    ungraded,
    face_grade,
    isStory,
    history,
    userActive,
    currentUser,
    enterKeyListner,
    in_progress=false
}, ref) => {
    const showAlert = useAlert();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { isAiPermissionEnabled } = useUserProfileConfig();
    const {createCollectionNotification} = useCollectionNotification();
    const [tempValue, setTempValue] = useState(value);
    const [tempFlag, setTempFlag] = useState(submission?.exceptionStatus || 0);
    const inputRef = useRef()
    const [dialogOpenn, setDialogOpenn] = useState(false)
    const hasUser = !!user?.id;
    const is_update = useRef(false)
    const inputDirtyRef = useRef(false)
    const wasMissingRef = useRef(submission?.exceptionStatus === 3)
    const [optimisticComplete, setOptimisticComplete] = useState(false);
    const optimisticCompleteRollbackTimerRef = useRef(null);
    const latestSubmissionCompleteRef = useRef(!!get(submission, "complete"));
    const isWorksheetInProgress = useMemo(() => {
        const assignmentType = get(submission, "assignmentType", get(submission, "type", get(assignment, "type")));
        return assignmentType === "worksheet" && get(submission, "id") && !get(submission, "complete");
    }, [submission, assignment]);
    const showInProgress = in_progress || isWorksheetInProgress;
    const {hasValidAiFeedback} = useGradeBook();

    const validAiFeedback = useMemo(() => {
        return hasValidAiFeedback(submission);
    }, [submission]);

    const { max_point, isCompleteInComplete, isPointSystem } = useMemo(() => {
        return ({
            max_point: assignment?.max_point && !isNaN(assignment?.max_point) ? Number.parseInt(assignment.max_point, 10) : 100,
            isCompleteInComplete: assignment?.grade_type === 'complete_incomplete',
            isPointSystem: assignment?.grade_type  === "points"
        })
    }, [assignment]);
    const has_to_gradeManually = useMemo(() => {
        if (!assignment?.data?.questions) return false;
        return checkIsManualGradable(assignment.data.questions)
      }, [assignment]);
    const editValue = () => {
        const input = document.getElementById(assignmentId + userId);
        if(input){
            input.focus();
            input.select();
        }
    };
    const setVal = () => {
        const input = document.getElementById(assignmentId + userId);
        if (input === document.activeElement && inputDirtyRef.current) {
            return;
        }
        if (isPointSystem && !gradebook_a_f) {
            const val = ((typeof face_grade === "number" && face_grade >= 0) || (typeof face_grade === "string" && face_grade !== "" && !Number.isNaN(Number.parseFloat(face_grade)) && Number.parseFloat(face_grade) >= 0)) ? face_grade : null;
            setTempValue(val);
        } else {
            setTempValue(value);
        }
        setTempFlag(submission?.exceptionStatus || 0);
        wasMissingRef.current = submission?.exceptionStatus === 3;
        inputDirtyRef.current = false;
    };
    useEffect(() => {
        setVal();
    }, [value, face_grade, gradebook_a_f, submission?.id, submission?.grade, submission?.face_grade, submission?.exceptionStatus, submission?.manualGrading, submission?.graded, submission?.complete, assignment?.max_point, assignment?.grade_type])

    useEffect(() => {
        setOptimisticComplete(false);
        if (optimisticCompleteRollbackTimerRef.current) {
            clearTimeout(optimisticCompleteRollbackTimerRef.current);
            optimisticCompleteRollbackTimerRef.current = null;
        }
    }, [assignmentId, userId, submission?.id])

    useEffect(() => {
        latestSubmissionCompleteRef.current = !!get(submission, "complete");
        if (get(submission, "complete")) {
            setOptimisticComplete(false);
            if (optimisticCompleteRollbackTimerRef.current) {
                clearTimeout(optimisticCompleteRollbackTimerRef.current);
                optimisticCompleteRollbackTimerRef.current = null;
            }
        }
    }, [submission?.complete])

    useEffect(() => {
        return () => {
            if (optimisticCompleteRollbackTimerRef.current) {
                clearTimeout(optimisticCompleteRollbackTimerRef.current);
            }
        };
    }, [])
    const handleChange = (e) => {
        e.preventDefault();
        inputDirtyRef.current = true;
        let _value = e.target.value.replace(/-/g, "");
        if (_value.startsWith('0') && _value.length > 1) {
            setTempValue('0');
            return;
        }
        const af = hasGradeInA_F(_value)
        const isAf = af.hasValue && !!gradebook_a_f;
        const isScale = scale_1_5 && _value <= 4 && _value >= 0
        const isAv = !scale_1_5 && !gradebook_a_f && _value <= 100 && _value >= 0
        if (isPointSystem) {
            if (typeof max_point === "number") {
                if (_value >= 0 && _value <= max_point) {
                    setTempValue(_value);
                    onupdate(_value);
                }
            } else {
                if (_value >= 0 && _value <= 100) {
                    setTempValue(_value);
                    onupdate(_value);
                }
            }
        } else if (isScale || isAf || isAv || _value === "") {
            setTempValue(_value);
            onupdate(_value);
        }
    };
    const updateGradetoSchoology = async (_val, statusCode, { feedbackText, manualGrading } = {}) => {
        if (currentClass && currentClass.sectionId && user && user.schoologyid) {
            await handleGradeToSchoology({
                sectionId:currentClass?.sectionId,schoologyid:currentUser.schoologyid , statusCode:statusCode, value:_val, studentSchoologyIds:[user.schoologyid], sAssignmentId:assignment.sAssignmentId,
                manualGrading,
                feedbackText
            })
        }
    }
    const updateGradetoGoogleClassRoom = async(_val) => {
        if (currentClass && currentClass.googleId && user && user.googleId) {
            try {
              await handleUpdateGoolgeGradeInBatch(currentUser.googleId,user.googleId,currentClass.googleId,assignments.googleId,_val)
            } catch (error) {
                Sentry.captureException(`588 Error in updateGradetoGoogleClassRoom: ${error}`);
                console.log(error, "issue in update grade to classroom");
            }
        }
    }
    const updateGradeToCanvas = async (_val, status) => {
        if (currentUser && currentUser.canvas_id && currentClass.canvas_id && assignments?.canvas_id) {
            if (status>0||status==1) {
                await handleUpdateCanvasStatus(currentUser.canvas_id,{
                    "canvas_assignment_id":assignments.canvas_id,
                    "course_id":currentClass.canvas_course_id,
                    "canvas_user_id":user.canvas_id,
                    "status": status
                },currentUser.canvas_auth_id)
            } else {
                await handleUpdateCanvasGradeInBatch(currentUser.canvas_id,{
                    "canvas_assignment_id": assignments?.canvas_id,
                    "course_id":currentClass.canvas_course_id,
                    "canvas_user_id":user.canvas_id,
                    "grade": _val
                },currentUser.canvas_auth_id)
            }
        }
        }

        const returnNotification = (submission) => {
            const notificationData = {
                id: v4(),
                classId: get(currentClass, 'id', ''),
                displayName: get(submission, 'assignment.displayName', ''),
                senderId: get(currentUser, 'id', ''),
                receiversId: [].concat(get(submission, 'userId', '')),
                modified: moment().toISOString(),
                data: {
                    chapter: get(submission, 'assignment.chapter', ''),
                    page: get(submission, 'assignment.page', ''),
                    id: get(submission, 'assignment.id', ''),
                },
                message: 'Your Teacher Returned this Assignment',
                type: 'update_quiz',
                userSeen: []
            }
            sendNotifications(notificationData);
        }
    const [returnLoading, setReturnLoading] = useState(false);    
    const returnAssignment = async () => {
        setReturnLoading(true); 
        try {
            if (submission && submission.id) {
                if(submission.returned ===submission.submissions+1){
                    showAlert(`Assignment already returned!`, "error")
                    return;
                }else if(isDueDatePassed()){
                    showAlert(`You can’t return this assignment because it is already past due. Please change the due date if you’d like to return the submission.`, "error")
                    return;
                }else if(!submission.complete){
                    showAlert(`You can’t return this assignment because it is in progress.`, "error")
                    return;
                }
                const v = await dispatch(handleReturnSubmission(submission.id))
                if (v && v.id) {
                    const returnedPayload = v.data || v;
                    const optimisticReturnedCount = Number.parseInt(get(returnedPayload, "returned", NaN), 10);
                    const currentSubmissionCount = Number.parseInt(get(submission, "submissions", 0), 10);
                    const effectiveReturned = Number.isNaN(optimisticReturnedCount) ? currentSubmissionCount + 1 : optimisticReturnedCount;
                    // reviewAssignment({
                    //     ...getReviewSubmission(),
                    //     ...returnedPayload,
                    //     returned: effectiveReturned,
                    //     submissions: get(returnedPayload, "submissions", get(submission, "submissions", 0)),
                    // });
                    const classId = get(currentClass, "id");
                    if (classId) {
                        await dispatch(handleListClassSubmissions(classId));
                    }
                    returnNotification(submission)
                    showAlert(`Assignment returned successfully.`, "success")
                } else {
                    showAlert(`Having issue while returning Assignment`, "error")
                }
            } else {
                showAlert(`Assignment is not submitted by student.`, "error")
            }
        } catch (error) {
            setReturnLoading(false);
            showAlert(`Having issue while returning Assignment`, "error")
        }finally{
            setReturnLoading(false);
        }
    }
    const updateToOther = (tempValue,_val) =>{
        const gradeForLms = isPointSystem ? tempValue : _val;
        const has_feedback = Boolean((submission?.feedback && String(submission.feedback).trim()) || submission?.audioFeedback);
        if (!has_feedback) {
            updateGradeToCanvas(gradeForLms)
            updateGradetoGoogleClassRoom(gradeForLms);
        }
        updateGradetoSchoology(gradeForLms, undefined, {
            feedbackText: has_feedback && submission?.feedback ? String(submission.feedback).trim() : undefined,
            manualGrading: has_feedback ? submission?.manualGrading : undefined,
        });
    }
    const updateToOtherDebounce = useMemo(
        () =>
            debounce((tempValue, _val) => {
                const gradeForLms = isPointSystem ? tempValue : _val;
                const has_feedback = Boolean((submission?.feedback && String(submission.feedback).trim()) || submission?.audioFeedback);
                if (!has_feedback) {
                    updateGradeToCanvas(gradeForLms);
                    updateGradetoGoogleClassRoom(gradeForLms);
                }
                updateGradetoSchoology(gradeForLms, undefined, {
                    feedbackText: has_feedback && submission?.feedback ? String(submission.feedback).trim() : undefined,
                    manualGrading: has_feedback ? submission?.manualGrading : undefined,
                });
            }, 2000),
        [isPointSystem, currentClass, currentUser, user, assignments, assignment, submission]
    );
    useEffect(() => () => updateToOtherDebounce.cancel(), [updateToOtherDebounce]);
    const onupdate = async (innerVal) => {
        let _val = innerVal;
        const hasInA_F = hasGradeInA_F(_val)
        if (isPointSystem) {
            if (_val==''||_val=='-') {
                return;
            }
            if (!(_val >= 0 && _val <= max_point)) {
                return;
            }
        }
        else if ((
            (!gradebook_a_f && isNaN(_val))) ||
            (scale_1_5 && _val > 4) ||
            (gradebook_a_f && !hasInA_F.hasValue) ||
            (!scale_1_5 && !gradebook_a_f && !isPointSystem  && _val > 100)
        ) {
            return;
        }

        if (scale_1_5 && _val <= 4) {
            _val = _val == 0 ? _val : `${(_val * 20) + 10}`
        }
        if (gradebook_a_f && hasInA_F.hasValue) {
            _val = hasInA_F.value
        }
        if (isPointSystem) {
            const per = (_val / max_point) * 100;
            _val = per;
        }

        tempData.grade = _val;
        tempData.graded = true;
        tempData.manualGrading = false;
        tempData.submissions = 1
        tempData.face_grade = innerVal
        const wasMissing = tempFlag === 3 || get(submission, "exceptionStatus") === 3 || wasMissingRef.current;
        if (wasMissing) {
            tempData.exceptionStatus = 0;
            setTempFlag(0);
            wasMissingRef.current = true;
        }
        if (_val === null || _val === '' || _val === '-' || _val === '--') return
        if (get(submission, "id")) {
            is_update.current = true;
            const dt = {
                id: submission.id,
                grade: _val,
                face_grade: innerVal,
                ...(wasMissing ? { exceptionStatus: 0 } : {})
            }
            void dispatch(handleBatchGradeSubmissions(dt, { immediate: false }))
            updateToOtherDebounce(innerVal,_val)
        }else {
            is_update.current = true;
            let dt = {
                 grade: _val, 
                 face_grade: innerVal,
                 assignmentId: tempData.assignmentId,
                 assignmentType: tempData.assignmentType,
                 chapter: tempData.chapter,
                 classId: tempData.classId,
                 orgId: currentUser.orgId,
                 userId: tempData.userId,
                 ...(wasMissing ? { exceptionStatus: 0 } : {})
            }
            void dispatch(handleBatchGradeSubmissions(dt, { immediate: false }))
            updateToOtherDebounce(innerVal,_val)
        }
    };
    const submit = async (e , on_force_update) => {
        const notificationData = {
            id: v4(),
            classId: get(currentClass, 'id', ''),
            displayName: get(tempData, 'assignment.displayName', ''),
            senderId: get(currentUser, 'id', ''),
            receiversId: [].concat(get(tempData, 'userId', '')),
            modified: moment().toISOString(),
            data: {
                chapter: get(tempData, 'assignment.chapter', ''),
                page: get(tempData, 'assignment.page', ''),
                id: get(tempData, 'assignment.id', ''),
                assignmentType: get(tempData, 'assignmentType', '') || '',
            },
            message: `Your Teacher Graded this ${get(tempData, 'assignmentType', '') === 'debate' ? 'Debate' : get(tempData, 'assignmentType', '') === 'game' ? 'Game' : 'Assignment'}`,
            type: 'update_grade',
            userSeen: []
        }
        e.preventDefault();
        e.stopPropagation();
        onBlur();
        let _val = tempValue;
        const hasInA_F = hasGradeInA_F(tempValue)
        if(isPointSystem){
            if(!on_force_update &&((tempValue === face_grade)||tempValue ==="-")){
                if (is_update.current) {
                    sendNotifications(notificationData);
                    is_update.current = false;
                }
                inputDirtyRef.current = false;
                return;
            }
             if(!on_force_update && !(tempValue>=0&&tempValue<=max_point)){
                if (is_update.current) {
                    sendNotifications(notificationData);
                    is_update.current = false;
                }
                inputDirtyRef.current = false;
                return;
             }
        }
        else if (!on_force_update && (
            (tempValue === value || tempValue === "-") ||
            (!gradebook_a_f && isNaN(tempValue)) ||
            (scale_1_5 && tempValue > 4) ||
            (gradebook_a_f && !hasInA_F.hasValue) ||
            (!scale_1_5 && !gradebook_a_f && tempValue > 100)
        )) {
            if (is_update.current) {
                sendNotifications(notificationData);
                is_update.current = false;
            }
            setTempValue(value);
            inputDirtyRef.current = false;
            return;
        }

        if (scale_1_5 && tempValue <= 4) {
            _val = tempValue == 0 ? tempValue : `${(tempValue * 20) + 10}`
        }
        if (gradebook_a_f && hasInA_F.hasValue) {
            _val = hasInA_F.value
        }
        if(isPointSystem){
            const per = (tempValue / max_point ) * 100;
            _val=per;
        }

        tempData.grade = _val;
        tempData.graded = true;
        tempData.manualGrading = false;
        tempData.submissions = 1
        tempData.face_grade = tempValue
        const wasMissing = tempFlag === 3 || get(submission, "exceptionStatus") === 3 || wasMissingRef.current;
        if (wasMissing) {
            tempData.exceptionStatus = 0;
            setTempFlag(0);
            wasMissingRef.current = true;
        }
        if(_val===null || _val==='' || _val ==='-' || _val ==='--') {
            inputDirtyRef.current = false;
            return;
        }
        const runAfterPaint = () => {
            queueMicrotask(() => {
                sendNotifications(notificationData);
            });
            startTransition(() => {
                setOptimisticComplete(true);
                if (optimisticCompleteRollbackTimerRef.current) {
                    clearTimeout(optimisticCompleteRollbackTimerRef.current);
                    optimisticCompleteRollbackTimerRef.current = null;
                }
                optimisticCompleteRollbackTimerRef.current = setTimeout(() => {
                    if (!latestSubmissionCompleteRef.current) {
                        setOptimisticComplete(false);
                    }
                    optimisticCompleteRollbackTimerRef.current = null;
                }, 4500);
            });
            updateToOtherDebounce(tempValue, _val);
            if (get(submission, "id")) {
                const dt = {
                    id: submission.id,
                    grade: _val,
                    face_grade: tempValue,
                    ...(wasMissing ? { exceptionStatus: 0 } : {})
                }
                void dispatch(handleBatchGradeSubmissions(dt, { immediate: false }))
            } else {
                let dt = {
                     grade: _val, 
                     face_grade: tempValue,
                     assignmentId: tempData.assignmentId,
                     assignmentType: tempData.assignmentType,
                     chapter: tempData.chapter,
                     classId: tempData.classId,
                     orgId: currentUser.orgId,
                     userId: tempData.userId,
                     ...(wasMissing ? { exceptionStatus: 0 } : {})
                }
                void dispatch(handleBatchGradeSubmissions(dt, { immediate: false }))
            }
            inputDirtyRef.current = false;
        };
        if (on_force_update) {
            requestAnimationFrame(runAfterPaint);
        } else {
            runAfterPaint();
        }
    };
    const updateStatus = async (status) => {
        tempData.grade = status == 3 ? 0 : null;
        tempData.graded = true;
        tempData.exceptionStatus = status;
        tempData.submissions = 1
        tempData.face_grade = status == 3 ? 0 : null
        let subCount = parseInt(get(data, "submissions", 1), 10);
        if (status == 0 && subCount > 0) {
            subCount = subCount - 1;
        } else if (status != 0 && subCount == 0) {
            subCount = subCount + 1;
        }
        if (get(submission, "id")) {
            const dt = {
                id: submission.id,
                grade: status == 3 ? 0 : null,
                exceptionStatus: status,
                submissions : subCount,
                face_grade : status == 3 ? 0 : null,
                manualGrading:false
            }
            await dispatch(handleUpdateGrade(dt))
        } else {
            await dispatch(handleUpdateSubmission(null, {
                answers: [],
                complete: true,
                grade: tempData.grade,
                submissions: subCount,
                retaking: false,
                graded: true,
                feedback:'',
                manualGrading: false,
                exceptionStatus: status,
                face_grade:tempValue
            }, tempData))
        }
        await dispatch(handleListClassSubmissions(tempData.classId));
    }
    useImperativeHandle(ref, () => ({
        focus: () => {
            editValue()
        },
        blur: () => {
            inputRef.current.blur();
        },
        isFocused: () => {
            const input = document.getElementById(assignmentId + userId);
            return input === document.activeElement;
        },
        ref: inputRef.current
    }));

    const graded = useMemo(() => {
        if (submission?.retaking) {
            return true;
        } else if (submission?.complete && submission?.graded) {
            return true;
        } else if (!submission) {
            return true;
        } else {
            return false;
        }
    },[submission]);
    const getNotificationStatus = () => {
        const message = get(tempData, 'exceptionStatus', '')
        if (message  == 1) {
           return `Your teacher turned this Assignment into ${currentUser.schoologyid?"Exempt":"Excused"}`
        } else if(message == 2){
            return `Your teacher turned this Assignment into Incomplete`
        } else if(message == 3){
            return `Your teacher turned this Assignment into Missing`
        }
    }
    const updateNotification = async () => {
        const notificationData = {
            id: v4(),
            classId: get(currentClass, 'id', ''),
            displayName: get(tempData, 'assignment.displayName', ''),
            senderId: get(currentUser, 'id', ''),
            receiversId: [].concat(get(tempData, 'userId', '')),
            modified: moment().toISOString(),
            data: {
                chapter: get(tempData, 'assignment.chapter', ''),
                page: get(tempData, 'assignment.page', ''),
                id: get(tempData, 'assignment.id', ''),
            },
            message: getNotificationStatus(),
            type: 'update_quiz',
            userSeen: []
        }
        sendNotifications(notificationData)
    }


    const sendNotifications = async (notificationData) => {
        try {

            createCollectionNotification(notificationData, get(currentClass, 'id', ''));
        } catch (error) {
            Sentry.captureException(`589 Error in sendNotifications: ${error}`);
            console.log(error, "error")
        }
    }
    const updateStatusCode = (status) => {
        menuVisibalityChange(false);
        if (tempFlag == status) {
            setDialogOpenn(true);
        } else {
            updateStatus(status)
            setTempFlag(status);
            updateGradetoSchoology(status == 3 ? 0 : null, status)
            updateNotification()
            updateGradeToCanvas(status == 3 ? 0 : null, status)
        }
    }
    const onAreeRemoveException = () => {
        updateStatus(0)
        setTempFlag(0);
        setDialogOpenn(false);
        updateGradetoSchoology(null, 0)
        updateGradeToCanvas(null,0)
    };
    const getCheckIcon = (status,isCcomlete,isInComplete) => {
        if (tempFlag == status) {
            return <Check style={{ color: COLOR.green100, fontSize: 15 }} />
        }else if(isCcomlete&&isCompleteInComplete){
            return <Check style={{ color: COLOR.green100, fontSize: 15 }} />;
        }else if(isInComplete&&isCompleteInComplete){
            return <Check style={{ color: COLOR.green100, fontSize: 15 }} />;
        }
         else {
            return null;
        }
    }
    const isDueDatePassed = () => {
        if (!assignment) return false;
        const classConfig = find(assignment.classes, ['id', currentClass?.id]);
        const diffConfig =  getConfigForDiff(classConfig,user?.id)
        if (diffConfig.dueDateDif) {
            return isBeforTime(diffConfig.dueDateDif);
        } else {
            return assignment.lateDate ? isBeforTime(assignment.lateDate) : isBeforTime(assignment.dueDate);
        }
    };
    const getCompleteInclompleteMark = ()=>{
        const isComplete= has(submission,"id")&& submission.grade>0;
        const isInComplete  = has(submission,"id")&& submission.grade==0;
        const isPastDue = isDueDatePassed()
        const isPassedIncomplete  = !has(submission,"id")&& isPastDue
          if(isComplete){
            return <Check style={{ color: COLOR.green100, fontSize: 18 }} />
          }else if(isInComplete || isPassedIncomplete ){
            return <Close style={{ color: COLOR.red, fontSize: 18 }} />
          }else{
            return <span  style={{ color: COLOR.gray100, fontSize: 18 }}>-</span>
          }
    }
    const getStatusIcon = useCallback((status, fromTab) => {
        if(isNaN(status)){
            if(status === "complete"){
               return  <Check style={{ color: COLOR.white, fontSize: 15 , marginRight:5 , marginTop:2 , backgroundColor:COLOR.green100 , borderRadius:20 }} />;
            }else if(status === "incomplete"){
                return  <Close style={{ color: COLOR.white , fontSize: 15 , marginRight:5 , marginTop:2 , backgroundColor:COLOR.red , borderRadius:20 }} />;
            }
        }if(status == 5){
            return <img src={AbsentSvg} style={{ marginRight: fromTab ? 0 : 5 , height : 20 }}/>
        }
        if(status == 6){
            return <img src={LateSvg} style={{ marginRight: fromTab ? 0 : 5 , height : 20 }}/>
        }
        else{
            return <Poligon bgColor={COLORSTATUS[status]} style={{ marginRight: fromTab ? 0 : 5 }} />
        }
    })
    const getStatusByCode = useCallback((status) => {
        if (status == 0) {
            return ''
        } else if (status == 1) {
            return currentUser.schoologyid ? "Exempt" : "Excused";
        } else if (status == 2) {
            return 'Incomplete'
        } else if (status == 3) {
            return 'Missing'
        } else {
            return ""
        }
    }, [])
    const menuVisibalityChange = (visiblity) => {
        onFlagMenuViewChange(visiblity)
    }
    const updateCompleteStatus = async(v)=>{
         if(v === "complete" ||v === "incomplete" ){
            const gvalue =   v === "complete" ? 100:0
            const mx_val = v === "complete" ? max_point : 0
            if (get(submission, "id")) {
                const dt = {
                    id: submission.id,
                    grade: gvalue,
                    face_grade:mx_val
                }
                await dispatch(handleUpdateGrade(dt))
            } else {
                tempData.grade = gvalue;
                tempData.graded = true;
                tempData.manualGrading = false;
                tempData.submissions = 1
                tempData.face_grade = mx_val
                await dispatch(handleUpdateSubmission(null, {
                    answers: [],
                    complete: true,
                    grade:  gvalue,
                    submissions: 1,
                    retaking: false,
                    graded: true,
                    feedback: '',
                    manualGrading: false,
                    face_grade:mx_val
                }, tempData))

            }
            if (!submission?.id) {
                await dispatch(handleListClassSubmissions(tempData.classId));
            }
            await handleUpdateCanvasGradeInBatch(currentUser.canvas_id,{
                "canvas_assignment_id": assignments?.canvas_id,
                "course_id":currentClass.canvas_course_id,
                "canvas_user_id":user.canvas_id,
                "grade": v,
            },currentUser.canvas_auth_id)
         }

    }
    const userNavForFLag = useCallback(() => {
        let FLAGS = [];
        if (currentUser.canvas_id) {
            if (isCompleteInComplete) {
                FLAGS = CANVAS_FLAG_WITH_COMPLETE;
            } else {
                FLAGS = CANVAS_FLAG;
            }

        } else if (currentUser.schoologyid) {
            FLAGS = SCHOOLOGY_ALLUSER_FLAG;
        } else {
            FLAGS = ALLUSER_FLAG
        }
      return {
        items: [
          {
            key: 'header',
            label: <span style={{ color: "black" }}>MARK WITH AN EXCEPTION</span>,
            disabled: true
          },
          {
            key: 'divider',
            label: (
                <div
                  style={{
                    borderTop: '1px solid black',
                    margin: '2px 0',
                    width: '100%'
                  }}
                />
              )
          },
          ...map(FLAGS, (flg, i) => ({
            key: flg.value,
            label: (
              <MenuItem>
                <CheckBox>{getCheckIcon(flg.value, flg.value === "complete" && has(submission, "id") && submission.grade > 0, flg.value === "incomplete" && has(submission, "id") && submission.grade == 0)}</CheckBox>{" "}
                {getStatusIcon(flg.value)} {flg.title}
              </MenuItem>
            ),
            onClick: () => {
              if(typeof flg.value === "number"){
                  updateStatusCode(flg.value);
              }else{
                  updateCompleteStatus(flg.value)
              }
            }
          }))
        ]
      };
    }, [tempFlag]);

    const getReviewSubmission = useCallback(() => {
        if(has(submission,"id")){
            return {
                ...submission,
                user: user,
                assignment: has(submission,"assignment") ? submission.assignment : get(assignments,"data"),
                ungraded: ungraded
            };
        }else{
            return {
                ...tempData,
                user: user,
                assignment: has(tempData,"assignment") ? tempData.assignment : get(assignments,"data"),
                ungraded: ungraded
            };
        }
    }, [submission, tempData, user, ungraded, assignments]);

    const visabilityNavForFLag = useCallback(() => {
        return {
          items: [
            {
              key: 'view',
              label: (
                <MenuItem>
                  View Submission
                </MenuItem>
              ),
              onClick: () => {
                reviewAssignment(getReviewSubmission())
              }
            },
            ...(isStory ? [{
              key: 'story',
              label: (
                <MenuItem>
                  View on 1WayStory gradebook
                </MenuItem>
              ),
              disabled: !has(submission,"id"),
              onClick: () => {
                const reviewPayload = getReviewSubmission();
                navigate("/story/gradebook", {
                  state: {
                    storyGradebookPrefetch: {
                      assignmentId: reviewPayload?.assignmentId,
                      classId: get(currentClass, "id"),
                    },
                  },
                });
              }
            }] : []),
            {
              key: 'return',
              label: (
                <MenuItem>
                  Return
                </MenuItem>
              ),
              onClick: () => {
                returnAssignment();
              }
            }
          ]
        };
    }, [submission, user, assignment, isStory, currentClass, navigate, getReviewSubmission, reviewAssignment]);


    const renderStatusText = useMemo(() => {
        const submissionsCount = parseInt(get(submission, "submissions", '0'), 10);
        const returnedCount = parseInt(get(submission, "returned", 0), 10);
        const hasSubmission = submission && Object.keys(submission).length > 0;
        if(!validAiFeedback && !showInProgress) {
            return null;
        }
        if(showInProgress) {
            return <StatusText style={{color: "orange"}}>In-progress</StatusText>;
        }
        else if (hasSubmission && submissionsCount === returnedCount - 1) {
            return <StatusText>Returned</StatusText>;
        }
        else if (hasSubmission && ungraded && submission.assignmentType === "bingo") {
            const isSubmitted = submission?.answers?.length > 0 ? submission?.answers?.some((el)=> el?.is_submitted):false;
            return isSubmitted ? <StatusText>Credit</StatusText> : <StatusText>{"-"}</StatusText>;
        }
        else if (hasSubmission && ungraded) {
            return <StatusText>Credit</StatusText>;
        }
        else if (tempFlag > 0) {
            return (<StatusText color={COLORSTATUS[tempFlag]}>{getStatusByCode(tempFlag)}</StatusText>)
        } else if (isTbg && !ungraded && !optimisticComplete) {
            let bg_text 
            const is_graphic_organizer = get(submission, "assignment.activity_type") === "graphic_organizer" || get(assignment, "data.activity_type") === "graphic_organizer";
            if (currentUser?.role === 'student') {
                bg_text = 'TBG';
            } else {
                // bg_text = submission&&submission.ai_grade&&isAiPermissionEnabled&&submission.ai_grade?.length > 0?"TBG - AI": "TBG";
                bg_text = is_graphic_organizer ? "TBG" : (isAiPermissionEnabled ? "TBG - AI" : "TBG");
            }
            return (<StatusText>{bg_text}</StatusText>)
        }
        if (isTbg && !ungraded && !optimisticComplete) {
            let bg_text 
            const is_graphic_organizer = get(submission, "assignment.activity_type") === "graphic_organizer" || get(assignment, "data.activity_type") === "graphic_organizer";
            if (currentUser?.role === 'student') {
                bg_text = 'TBG';
            } else {
                // bg_text = submission&&submission.ai_grade&&isAiPermissionEnabled&& submission.ai_grade?.length > 0?"TBG - AI": "TBG";
                bg_text = is_graphic_organizer ? "TBG" : (isAiPermissionEnabled ? "TBG - AI" : "TBG");
            }
            return is_update.current ? '' : <StatusText>{bg_text}</StatusText>;
        }
        if (isLate && !ungraded) {
            const createdAt = submission?.createdAt;
            if (createdAt) {
                const date = moment(parseInt(createdAt));
                if (date.isValid()) {
                    return <StatusText>Done late{` (${date.format('MMM DD')})`}</StatusText>;
                }
            }
            return <StatusText>Done late</StatusText>;
        }
        if (!has_to_gradeManually &&!ungraded && submissionsCount > 0 && submissionsCount === returnedCount) {
            const createdAt = submission?.createdAt;
            if (createdAt) {
                const date = moment(parseInt(createdAt));
                if (date.isValid()) {
                    return <StatusText>Resubmitted {`(${date.format('MMM DD')})`}</StatusText>;
                }
            }
            return <StatusText>Resubmitted</StatusText>;
        }
        if (ungraded) {
            return <StatusText>{"-"}</StatusText>;
        }
        return null;
    }, [tempFlag, isTbg, ungraded, isLate, submission, optimisticComplete, is_update.current, has_to_gradeManually, isAiPermissionEnabled, showInProgress]);


    const renderResubmittedStatusText = useMemo(() => {
        const submissionsCount = parseInt(get(submission, "submissions", '0'), 10);
        const returnedCount = parseInt(get(submission, "returned", 0), 10);
        const isTbgAiStatus = isTbg && !ungraded && isAiPermissionEnabled && currentUser?.role !== 'student';
        if(isLate && !ungraded && isTbgAiStatus){
            return <ResubmittedStatusText>Done late-({moment(parseInt(submission?.createdAt)).format('MMM DD')})</ResubmittedStatusText>;
        }
        else if (!ungraded && submissionsCount > 0 && submissionsCount === returnedCount && has_to_gradeManually) {
            return <ResubmittedStatusText>{isLate? "Done late" : "Resubmitted"}-({moment(parseInt(submission?.createdAt)).format('MMM DD')})</ResubmittedStatusText>;
        }
        return null;
    }, [ungraded, submission, has_to_gradeManually, isLate, isTbg, isAiPermissionEnabled, currentUser]);

    const isDisable = useMemo(() => (value === '--' || isReviewAccount || disabled || (tempFlag > 0 && tempFlag !== 3)), [value, isReviewAccount, disabled, tempFlag]);
   const isDisabled  =  (disabled && tempFlag <= 0 )|| !hasUser

    const getValue = () => {
        if (ungraded) return "-";
        else if (tempFlag == 0 || !tempFlag) {
            if (tempValue === null || tempValue === "NULL") {
                return '-';
            }
            return (typeof tempValue === "number" && tempValue >= 0) || (typeof tempValue === "string") ? tempValue : '-';
        } else if (tempFlag == 3) {
            if (gradebook_a_f) return 'F';
            return '0';
        } else {
            return "--";
        }
    }
    const handleDialogClose = useCallback(() => {
        setDialogOpenn(false);
    }, []);
    const DialogContentElement = useMemo(
        () => (
            <Dialog
            open={dialogOpenn}
            onClose={handleDialogClose}
            aria-labelledby="alert-dialog-title"
            aria-describedby="alert-dialog-description"
            style={{ backgroundColor: "transparent" }}
        >
            <DialogTitle id="alert-dialog-title">{"Remove this flag?"}</DialogTitle>
            <DialogContent>
                <DialogContentText id="alert-dialog-description" style={{color:"rgba(0, 0, 0, 0.85)"}}>
                    Once you remove this flag, student grade will be deleted.
                    Are you sure you want to remove the flag?
                </DialogContentText>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleDialogClose} color="primary" type="button" style={{ opacity: 1, width: 100, backgroundColor: COLOR.red }}  >
                    Disagree
                </Button>
                <Button onClick={onAreeRemoveException} color="secondary" autoFocus style={{ opacity: 1, width: 100, backgroundColor: COLOR.green1000 }}>
                    Agree
                </Button>
            </DialogActions>
        </Dialog>
        ),
        [dialogOpenn, handleDialogClose]
    );
    const onFormSubmit = useCallback((e)=>{
        e.preventDefault();
        e.stopPropagation();
        const input = document.getElementById(assignmentId + userId);
        if(input)
        input.blur();
    },[assignmentId,userId])
    return (<>
        <FullScreenLoader isLoading={returnLoading} />
        {DialogContentElement}
        <Form
            disabled={isDisabled}
            onSubmit={onFormSubmit}
            graded={graded}
        >
             <Dropdown menu={userNavForFLag()}
                placement="bottomLeft" trigger={['hover']}
                onOpenChange={menuVisibalityChange}
                disabled={isDisabled || ungraded }
            >
                {tempFlag > 0 ?
                    <Button
                    userActive={userActive}
                    disabled={isDisabled || ungraded }
                    onClick={e => {
                        e.preventDefault()
                        e.stopPropagation()
                    }} style={{ marginRight: 5, opacity: 1, backgroundColor: "transparent", color: COLOR.black }} >
                        {getStatusIcon(tempFlag, true)}
                    </Button>
                    : <Button 
                    userActive={userActive}
                     disabled={isDisabled || ungraded }
                     onClick={e => {
                        e.preventDefault()
                    }} style={{ marginRight: 5 }}>
                        <Flag style={{ fontSize: '16px' }} />
                    </Button>}
            </Dropdown> 
            {!isCompleteInComplete ? renderResubmittedStatusText :null}
            {!isCompleteInComplete ?
                <Input
                disabled={!validAiFeedback || isDisable || !hasUser || ungraded || isCompleteInComplete}
                id={assignmentId + userId}
                onBlur={(e) => {
                    try {
                        if (e.target.value === '' || e.target.value === null || e.target.value === '-' || e.target.value ==='--') {
                            e.target.value = getValue()
                            setVal()
                            return;
                        }
                        const originalValue = isPointSystem && !gradebook_a_f ? face_grade : value;
                        if (e.target.value != originalValue && e.target.value !== '-' && e.target.value !== '--') {
                            void submit(e)
                        } else {
                            is_update.current = false;
                        }
                    } finally {
                        inputDirtyRef.current = false;
                    }
                }}
                onClick={editValue}
                onChange={handleChange}
                onFocus={e => {
                    if (e.target.value === '-') {
                        e.target.value = '';
                    }
                    e.target.select();
                    onFocus(e)
                }}
                onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.code === 'Enter' || e.keyCode === 13) {
                        e.preventDefault();
                        const isEmpty = e.target.value === '' || e.target.value === '-' || e.target.value === '--';
                        if (isEmpty) {
                            e.stopPropagation();
                            if (typeof enterKeyListner === "function") enterKeyListner();
                            return;
                        }
                        if (typeof enterKeyListner === "function") enterKeyListner();
                        void submit(e, true);
                    }
                }}
                className='cell-input'
                ref={inputRef}
                value={!validAiFeedback? "-" : getValue()}
                style={{ color: 'black' }}
            /> : <CheckBox>{getCompleteInclompleteMark()}</CheckBox>}
            {!isCompleteInComplete ? renderStatusText :null}
            {isPointSystem?<MaxPointText>{`/${max_point}`}</MaxPointText>:null}
            <Dropdown menu={visabilityNavForFLag()}
                placement="bottomLeft" trigger={['hover']}
                disabled={isDisabled}
            >
                <Button userActive={userActive} disabled={isDisabled} onClick={(e) => e.preventDefault()} type="button">
                    <MoreVert style={{ fontSize: '16px' }} />
                </Button>
            </Dropdown>
        </Form>
    </>
    );
});


export default GradeEditor;
